/* eslint-disable indent */
/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import StaticAddColumnDropdownComponent from './StaticAddColumnDropdownComponent';
import StatusBadgeScroller from './StatusBadgeScroller';
import SwapPropDropdown from './SwapPropDropdown';
import Text from '../../../../shared/components/Text/Text';
import {
 updateProgrammedCalendarItem,
 updateReduxCalendarItem,
 updateReduxProgramItem,
 swapProgrammedItemOnChildCalendar,
 setCombineWithInfo,
 updateSavedWorkoutItem,
} from '../../ducks/calendarActions';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import useSessionState from './hooks/useSessionState';
import { getDebouncedHandlerForTable, flushDebounceForTable } from './hooks/debounceHandler';
import { useFlushDebounceOnMouseMove } from './hooks/useFlushDebounceOnMouseMove';
import { normalizeRepToken } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';

const RANGE_FIELDS = new Set(['load']);
const INTEGER_FIELDS = new Set(['%pr']);

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};
  }
`;

const RowValueContainer = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 160px;
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3px;
`;

/** Transposed-layout styled components — properties as rows, sets as columns. */
const SetsCountHeader = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  /** Left padding matches TransposedScroll so the stepper aligns.
   * Bottom padding reserves room for the out-of-flow Sets subtext. */
  padding: 20px 0 22px 6px;
`;

/** Wrapper so the "# of Sets: 1-10" subtext sits under the stepper
 * without shifting it (subtext is taken out of flow). */
const SetsFieldGroup = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const SetsSubtext = styled('div')`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  white-space: nowrap;
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StateBadge = styled('div')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 8px;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: 11px;
  white-space: nowrap;
`;

const SetsCountLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

/** Pill-style stepper: a single bordered group with -, number, + segments.
 * Buttons disable at the 1/10 floor and ceiling but the input remains
 * typeable for power users who want to jump straight to a value. */
const SetsStepper = styled('div')`
  display: inline-flex;
  align-items: stretch;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surfaceInput};
  overflow: hidden;
`;

const StepperButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: ${TransitionGlobal};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHoverStrong};
  }
  /** Mouse-focus: stay quiet so clicks don't leave a ring.
   * Keyboard-focus: subtle inset highlight so users can see the
   * stepper button under their cursor. */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: none;
    background: ${FOCUS_FILL_VAR};
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.textDisabled};
    cursor: not-allowed;
  }
`;

const StepperInput = styled('input')`
  width: 35px;
  border: none;
  border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: transparent;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus {
    outline: none;
  }
`;

const TransposedScroll = styled('div')`
  width: 100%;
  overflow-x: auto;
  /** Left inset so the first label's focus highlight isn't clipped. */
  padding-left: 6px;
`;

const TransposedTable = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/** Rows fill the container; cells flex-grow into spare width, but their
 * own `min-width: 80px` floors the shrink so once the row reaches
 * `120 + 80*N + gaps`, further narrowing pushes content past the parent
 * `TransposedScroll` and horizontal scroll engages. We deliberately do
 * NOT use `min-width: max-content` here — inputs report a large
 * intrinsic width (default `size=20`) which would inflate the row and
 * keep cells from shrinking. */
const TransposedHeaderRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  width: 100%;
`;

const TransposedRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  width: 100%;
`;

const PropertyLabelCell = styled('div')`
  display: flex;
  align-items: center;
  min-width: 120px;
  max-width: 120px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
`;

const SetHeaderCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 80px;
  min-width: 80px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 400;
  /* Set labels read as body text in light (the redesign darkens every label) but
     stay secondary in dark, where full-strength white would outweigh the values. */
  color: ${({ theme }) => (theme.isDark ? theme.colors.textSecondary : theme.colors.text)};
`;

/** Per-set cell. Grows to fill extra width when the container is wider
 * than 120 + 80 * N; bottoms out at 80px each so the row pushes past
 * the scroll wrapper instead of shrinking further. We zero-out the
 * descendant min-widths because (a) the legacy `RowValueContainer`
 * carries `min-width: 160px` from the row-per-set layout, and (b) the
 * `<input>` inside has a default `size=20` which gives it a large
 * intrinsic min content width that would otherwise prevent shrink. */
const SetCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 80px;
  min-width: 80px;
  > div {
    min-width: 0;
    width: 100%;
  }
  input {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TransposedFooter = styled('div')`
  display: flex;
  align-items: center;
  padding-top: 12px;
`;

const AddPropertyTrigger = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px dashed ${({ theme }) => theme.colors.borderMedium};
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: ${TransitionGlobal};
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const AddPropertyTriggerIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
`;

/**
 * Transposed edit table for an assigned **tag** item — properties as rows,
 * one column per set. The tag twin of `ExerciseTableTransposed`, rendered by
 * `DraggableContent` when the `transposedEditTable` flag is on; `TagTable` is
 * the row-per-set layout used when it's off.
 *
 * The layout, property switches, sets stepper and cell validation are
 * identical to the lift table. Tags differ only in how the item is
 * identified when saving: the payload carries `type: 'tag'` and `tagId`
 * (never `exerciseId`), and the swap dropdown replaces `content.tag`
 * rather than `content.exercise`.
 */
const TagTableTransposed = ({
  content,
  index,
  coachCompletion,
  completionOnly,
  isLeftAndRight,
  trackRepCount,
  trackVolumeLoad,
  forcePrUpdate,
  disablePrUpdate,
  swapDropdownSelectedExercise,
  nonSwappedItemFromParentCalendar,
  isRecurring,
  measure,
  date,
  dayNumber,
}) => {
  const { colors } = useTheme();
  const dispatch = useDispatch();

  // Generate unique IDs for tooltips
  const repsTooltipId = `reps-tooltip-${content.assignedId}`;
  const timeTooltipId = `time-tooltip-${content.assignedId}`;
  const peakPowerTooltipId = `peak-power-tooltip-${content.assignedId}`;
  const velocityTooltipId = `velocity-tooltip-${content.assignedId}`;
  const restTooltipId = `rest-tooltip-${content.assignedId}`;

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
      (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  /** Update-instance option is fixed to 'single'; the picker UI is not
   * rendered in the transposed layout. */
  const selectedUpdateOption = { value: 'single' };
  const [primaryRows, setPrimaryRows] = useState([
    { name: 'set' },
    { name: content?.repType === 'time' ? 'time' : 'reps' },
    { name: 'load' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'tempo' },
    { name: 'velocity' },
    { name: 'peak power', label: 'Peak Power (w)' },
    { name: '%pr' },
  ]);

  const [tempo, setTempo] = useState(null);
  const [rest, setRest] = useState(null);
  const [velocity, setVelocity] = useState(content?.trackBarSpeed || false);
  const [peakPowerEnum, setPeakPowerEnum] = useState(content?.trackPeakPower);
  const [isBodyweight, setIsBodyweight] = useState(!!content?.repsOnly);
  const [isTime, setIsTime] = useState(content?.repType === 'time');

  const [firstPrimaryRowSwitchLoad, setFirstPrimaryRowSwitchLoad] = useState(true);
  const [firstContentLoad, setFirstContentLoad] = useState(true);
  const [firstLoadDropdownOptions, setFirstLoadDropdownOptions] = useState(true);

  /** we need to use session storage for primary row switch values so that if the user adds a field
   * it will not get wiped out between debounces if they haven't entered a value yet, changes to individual table properties
   *  will persist the session
   */
  const [primaryRowSwitch, setPrimaryRowSwitchState] = useSessionState(
    /** Deliberately the same key `TagTable` uses, so a coach's property
     * visibility carries over when the transposed flag flips. */
    'primaryRowSwitchTagTable',
    content?.assignedId,
    {
      set: true,
      [content?.repType === 'time' ? 'time' : 'reps']: true,
      load: false,
      rest: !!content?.restTime || false,
      tempo: false,
      velocity,
      'peak power': content?.trackPeakPower || false,
    },
  );

  const keySwapDictionary = {
    load: 'prescribedValue',
    '%pr': 'percent',
    set: 'setNumber',
    reps: 'reps',
    time: 'reps',
  };

  const [rows, setRows] = useState(content?.sets || []);

  /** Draft string shown while the Sets field is focused. Editing only touches
   * this draft; the real set count is committed once on blur/Enter so that
   * typing a two-digit count (e.g. "10") never transiently collapses to 1 set
   * and destroys the per-set values in between keystrokes. null = not editing,
   * so the input mirrors rows.length. */
  const [setsDraft, setSetsDraft] = useState(null);

  // Ref for the table container, used to scope keyboard shortcuts
  const tableContainerRef = useRef(null);

  const formatDate = () => `${moment(date).format('YYYY-MM-DD')}`;

  const hasMounted = useRef(false);

  /** Re-measure the virtualized list cell whenever something changes
   * the *vertical* height of this exercise card. In the transposed
   * layout, sets are columns so changing the set count grows the table
   * horizontally only — vertical height is driven by:
   *   - Property visibility (primaryRowSwitch: add/remove rows)
   *   - The kebab-toggle status pills wrapping in the header
   * Skip the first run because the CellMeasurer wrapper already
   * triggers its initial measure on mount. */
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (measure) {
      measure();
    }
  }, [
    primaryRowSwitch,
    isLeftAndRight,
    coachCompletion,
    completionOnly,
    forcePrUpdate,
    disablePrUpdate,
    trackVolumeLoad,
    trackRepCount,
  ]);

  /** debounced function will maintain values at the time of creation
     * on mount unless passed updated values, we can't to make sure we are
     * always using the most updated selected date and current user
     */
  const currentUserRef = useRef(currentUser);
  const selectedDateRef = useRef(date);
  const dayNumberRef = useRef(dayNumber);
  useEffect(() => {
    currentUserRef.current = currentUser;
    selectedDateRef.current = date;
    dayNumberRef.current = dayNumber;
  }, [currentUser, date, dayNumber]);

  /** An item inherited from a parent calendar detaches onto the child on its
   * first edit, so the 4s debounce is flushed as soon as the coach moves on —
   * otherwise a pending save can land after a drag and fight the new order. */
  useFlushDebounceOnMouseMove(content.assignedId, nonSwappedItemFromParentCalendar);

  const debouncedHandleUpdateLiftItem = getDebouncedHandlerForTable(content.assignedId, (
    updatedRows,
    dbrest,
    dbtempo,
    dbPeakPower,
    dbVelocity,
    dbCompletionOnly,
    dbCoachCompletion,
    dbIsLeftAndRight,
    dbTrackRepCount,
    dbTrackVolumeLoad,
    dbIsBodyweight,
    dbIsTime,
    dbForcePrUpdate,
    dbDisablePrUpdate,
    dbContent,
    dbIndex,
    dbNonSwappedItemFromParentCalendar,
    reorder = false,
    // Kept for call-site positional compatibility (other callers in this file
    // still pass selectedUpdateOption.value here) - no longer read now that
    // batchId threading replaced its use in the dispatch calls below, mirroring
    // ExerciseTableTransposed.js.
    // eslint-disable-next-line no-unused-vars
    updateOption = 'single',
    dbBatchId = undefined,
  ) => {
    const updateItem = {
      assignedId: dbContent.assignedId,
      /** After a tag swap the item is rebuilt with a flat `tagId`, so fall
       * back to it when the nested `tag` object isn't the current one. */
      tagId: dbContent?.tag?.id || dbContent?.tagId,
      order: dbIndex,
      sets: updatedRows,
      groupingLetter: dbContent.groupingLetter,
      coachCompletion: dbCoachCompletion,
      completionOnly: dbCompletionOnly,
      eachSide: dbIsLeftAndRight,
      description: dbContent?.exercise?.description || '',
      additionalInfo: dbContent?.additionalInfo || null,
      type: 'tag',
      trackPeakPower: dbPeakPower,
      trackRepCount: dbTrackRepCount,
      trackVolumeLoad: dbTrackVolumeLoad,
      repsOnly: dbIsBodyweight,
      repType: dbIsTime ? 'time' : 'reps',
      forceMaxUpdate: dbForcePrUpdate,
      maxTrackingDisabled: dbDisablePrUpdate,
    };
    if (programType !== 'free-form-program') {
      if (!isRecurring) {
        updateItem.date = moment(selectedDateRef.current).format('YYYY-MM-DD');
      } else {
        updateItem.isRecurring = isRecurring;
      }
    }
    if (programType === 'free-form-program') {
      updateItem.dayNumber = dayNumberRef.current;
    }
    if (dbrest) {
      updateItem.restTime = dbrest;
    }
    if (dbtempo) {
      updateItem.tempo = dbtempo;
    }
    if (dbVelocity) {
      updateItem.trackBarSpeed = dbVelocity;
    }
    if (programType === 'free-form-program') {
      dispatch(updateSavedWorkoutItem(currentUserRef.current.accountCode, updateItem, dbIndex, dayNumberRef.current, reorder));
    }
    if (programType === 'calendar') {
      if (dbNonSwappedItemFromParentCalendar) {
      dispatch(swapProgrammedItemOnChildCalendar(currentUserRef.current.accountCode, updateItem, dbIndex, formatDate(selectedDateRef.current), reorder, { batchId: dbBatchId }));
    } else {
      dispatch(updateProgrammedCalendarItem(currentUserRef.current.accountCode, updateItem, dbIndex, formatDate(selectedDateRef.current), reorder, { batchId: dbBatchId }));
    }
    }
  });

  /** This useEffect listens for changes is the exercise chosen from the
   * table dropdown menu that can be used to change the exericse
   * it immediately updates the item in redux so we see the change in the table
   * immediately... we need to re create the object to pass to redux, then we call the debouce update the same way we do
   * for every other input change
   */

  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...content,
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(date),
        tagId: swapDropdownSelectedExercise.exerciseId,
        /** additionalInfo: swapDropdownSelectedExercise?.additionalInfo, */
        description: swapDropdownSelectedExercise?.description,
        order: index,
        sets: rows,
        tag: swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise?.name,
        coachCompletion,
        completionOnly,
        eachSide: isLeftAndRight,
        type: 'tag',
        icon: 'tag',
        trackPeakPower: peakPowerEnum,
        trackRepCount,
        trackVolumeLoad,
        repsOnly: isBodyweight,
        repType: isTime ? 'time' : 'reps',
        forceMaxUpdate: forcePrUpdate,
        maxTrackingDisabled: disablePrUpdate,
      };
      if (rest) {
        updatedItem.restTime = rest;
      }
      if (!rest) {
        delete updatedItem.restTime;
      }
      if (tempo) {
        updatedItem.tempo = tempo;
      }
      if (velocity) {
        updatedItem.trackBarSpeed = velocity;
      }
      if (programType === 'free-form-program') {
      dispatch(updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, index));
      } else {
      dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, index));
      }
      /** Drop any pending pre-swap payload so it can't land after the swap
       * and re-save the old tag's values (kept from `TagTable`). */
      debouncedHandleUpdateLiftItem.cancel();
      debouncedHandleUpdateLiftItem(
        rows,
        rest,
        tempo,
        peakPowerEnum,
        velocity,
        completionOnly,
        coachCompletion,
        isLeftAndRight,
        trackRepCount,
        trackVolumeLoad,
        isBodyweight,
        isTime,
        forcePrUpdate,
        disablePrUpdate,
        updatedItem,
        index,
        nonSwappedItemFromParentCalendar,
        false,
        selectedUpdateOption.value,
      );
      flushDebounceForTable(content.assignedId);
    }
  }, [swapDropdownSelectedExercise]);

  useEffect(() => {
    /** handle check for array of sources */
    const isSource = combineWithInfo?.source?.length
    ? combineWithInfo.source.some((item) => item?.assignedId.toString() === content?.assignedId.toString())
    : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );
    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));
      const updateItem = {
        assignedId: content.assignedId,
        /** This object is handed to the debounced updater as its `dbContent`,
         * so it has to carry the tag identity forward itself. */
        tagId: content?.tag?.id || content?.tagId,
        tag: content?.tag,
        order: index,
        sets: rows,
        groupingLetter: isSource
          ? sourceItem?.groupingLetter
          : isTarget
            ? targetItem?.groupingLetter
            : content?.groupingLetter,
        groupingColorCode: isSource
          ? sourceItem?.groupingColorCode
          : isTarget
            ? targetItem?.groupingColorCode
            : content?.groupingColorCode,
        coachCompletion,
        completionOnly,
        eachSide: isLeftAndRight,
        description: content?.exercise?.description || '',
        additionalInfo: content?.additionalInfo,
        type: content.type,
        trackPeakPower: peakPowerEnum,
        trackRepCount,
        trackVolumeLoad,
        repsOnly: isBodyweight,
        repType: isTime ? 'time' : 'reps',
        forceMaxUpdate: forcePrUpdate,
        maxTrackingDisabled: disablePrUpdate,
      };
      if (content.restTime) {
        updateItem.restTime = content.restTime;
      }
      if (content.tempo) {
        updateItem.tempo = content.tempo;
      }
      if (content.trackBarSpeed) {
        updateItem.trackBarSpeed = content.trackBarSpeed;
      }
      /** Only the combine target should trigger the position reorder - every
       * source row hitting its own updateCalendarItemsOrder call too would
       * fire N redundant reorder PUTs for one logical move (mirrors the fix
       * applied to the lift-family Transposed tables). A MULTI-item combine
       * shares one batchId (combineWithInfo.sharedBatchId) so the whole
       * gesture is one undoable action; when it's unset (single-item toggle)
       * combineBatchId stays undefined and the update/swap thunk falls
       * through to currentBatchId, matching a normal field edit. */
      const reorder = isTarget;
      const combineBatchId = combineWithInfo.sharedBatchId;
      debouncedHandleUpdateLiftItem(
        rows,
        rest,
        tempo,
        peakPowerEnum,
        velocity,
        completionOnly,
        coachCompletion,
        isLeftAndRight,
        trackRepCount,
        trackVolumeLoad,
        isBodyweight,
        isTime,
        forcePrUpdate,
        disablePrUpdate,
        updateItem,
        index,
        nonSwappedItemFromParentCalendar,
        reorder,
        selectedUpdateOption.value,
        combineBatchId,
      );
      flushDebounceForTable(content.assignedId);
    }
  }, [combineWithInfo]);

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [focusTarget, content.assignedId]);

  /**
   * This is a useEffect that ensures that a. the various upper level dropdown options
   * being set to their default state doesn't trigger a put request, and b. subsequent
   * changes to the associated dropdown options DO trigger a put request
   */
  useEffect(() => {
    if (!firstLoadDropdownOptions) {
      debouncedHandleUpdateLiftItem(
        rows,
        rest,
        tempo,
        peakPowerEnum,
        velocity,
        completionOnly,
        coachCompletion,
        isLeftAndRight,
        trackRepCount,
        trackVolumeLoad,
        isBodyweight,
        isTime,
        forcePrUpdate,
        disablePrUpdate,
        content,
        index,
        nonSwappedItemFromParentCalendar,
        false,
        selectedUpdateOption.value,
      );
    } else {
      setFirstLoadDropdownOptions(false);
    }
  }, [
    coachCompletion,
    completionOnly,
    isLeftAndRight,
    trackRepCount,
    trackVolumeLoad,
    isBodyweight,
    isTime,
    peakPowerEnum,
    velocity,
    forcePrUpdate,
    disablePrUpdate,
  ]);

  // Track if all optional columns are enabled to hide the add column button
  const [showAddColumnButton, setShowAddColumnButton] = useState(true);

  // Hide add column button when all optional properties are enabled
  useEffect(() => {
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
      && primaryRowSwitch.tempo
      && primaryRowSwitch.velocity
      && primaryRowSwitch['peak power']
      && primaryRowSwitch['%pr']
      && primaryRowSwitch.load;

    setShowAddColumnButton(!allOptionalPropertiesEnabled);
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch.tempo,
    primaryRowSwitch.velocity,
    primaryRowSwitch['peak power'],
    primaryRowSwitch['%pr'],
    primaryRowSwitch.load,
  ]);

  /**
 * Updates the state of the primary row switch toggle and optionally modifies the rows array
 * based on the updated state. This function ensures that deactivated rows are removed
 * and the corresponding state for `tempo`, `rest`, `peak power`, and `velocity` is updated.
 * It also triggers the debounced update function to sync changes with the backend.
 *
 * Key features:
 * - Prevents unnecessary PUT requests on the initial load by managing a
 * `firstPrimaryRowSwitchLoad` flag.
 * - Deletes inactive fields from each row in the `rows`
 * array when their corresponding switch is toggled off.
 * - Updates `tempo`, `rest`, `peak power`, and `velocity` states as needed.
 * - Invokes a debounced backend update with the updated rows and state.
 */
  const setPrimaryRowSwitch = (newState) => {
    setPrimaryRowSwitchState((prevState) => {
      const updatedState = { ...prevState, ...newState };

      if (!firstPrimaryRowSwitchLoad) {
        setFirstPrimaryRowSwitchLoad(false);

        // Check if any columns are being removed (turned from true to false)
        const columnsBeingRemoved = Object.keys(newState).some((key) => prevState[key] === true && newState[key] === false);

        const initializedRows = [...rows];
        const updatedRows = initializedRows.map((row) => {
          const cleanedRow = { ...row };
          Object.keys(newState).forEach((key) => {
            const field = keySwapDictionary[key];
            if (!field || newState[key]) return;
            // reps and time both map to the 'reps' row field. Don't delete the
            // field if a sibling key sharing it is still true in the merged
            // next state (handles reps<->time swaps correctly).
            const fieldStillNeeded = Object.entries(keySwapDictionary).some(
              ([otherKey, otherField]) => otherKey !== key && otherField === field && updatedState[otherKey],
            );
            if (!fieldStillNeeded) {
              delete cleanedRow[field];
            }
          });
          return cleanedRow;
        });
        setRows(updatedRows);

        let newTempoValue;
        if (!newState.tempo) {
          setTempo(null);
          newTempoValue = null;
        } else {
          newTempoValue = tempo;
        }
        let newRestValue;
        if (!newState.rest) {
          setRest(null);
          newRestValue = null;
        } else {
          newRestValue = rest;
        }
        let bodyweightSetting;
        if (!newState.bodyweight) {
          setIsBodyweight(false);
          bodyweightSetting = false;
        } else {
          setIsBodyweight(true);
          bodyweightSetting = true;
        }
        let timeSetting;
        if (!newState.time) {
          setIsTime(false);
          timeSetting = false;
        } else {
          setIsTime(true);
          timeSetting = true;
        }
        let peakPowerEnumSetting;
        if (!newState['peak power']) {
          setPeakPowerEnum(0);
          peakPowerEnumSetting = 0;
        } else {
          setPeakPowerEnum(1);
          peakPowerEnumSetting = 1;
        }

        const updatedVelocity = newState.velocity;
        setVelocity(updatedVelocity);

        // Always call API when columns are being removed, otherwise check for empty fields
        if (columnsBeingRemoved) {
          // If columns are being removed, always trigger the API call
          debouncedHandleUpdateLiftItem(
            updatedRows,
            newRestValue,
            newTempoValue,
            peakPowerEnumSetting,
            updatedVelocity,
            completionOnly,
            coachCompletion,
            isLeftAndRight,
            trackRepCount,
            trackVolumeLoad,
            bodyweightSetting,
            timeSetting,
            forcePrUpdate,
            disablePrUpdate,
            content,
            index,
            nonSwappedItemFromParentCalendar,
            false,
            selectedUpdateOption.value,
          );
        } else {
          // For adding columns, check if any fields are empty before making API call
          // Exclude 'load' field from empty validation - load can be empty
          const fieldsToCheck = Object.keys(newState).filter((key) => newState[key] && keySwapDictionary[key] && key !== 'load');
          const hasEmptyFields = fieldsToCheck.some((key) => {
            const fieldKey = keySwapDictionary[key];
            return updatedRows.some((row) => {
              const fieldValue = row[fieldKey];
              return !fieldValue || fieldValue === null || fieldValue === '';
            });
          });

          // Only call the API if no fields are empty or if no trackable fields are active
          if (!hasEmptyFields || fieldsToCheck.length === 0) {
            debouncedHandleUpdateLiftItem(
              updatedRows,
              newRestValue,
              newTempoValue,
              peakPowerEnumSetting,
              updatedVelocity,
              completionOnly,
              coachCompletion,
              isLeftAndRight,
              trackRepCount,
              trackVolumeLoad,
              bodyweightSetting,
              timeSetting,
              forcePrUpdate,
              disablePrUpdate,
              content,
              index,
              nonSwappedItemFromParentCalendar,
              false,
              selectedUpdateOption.value,
            );
          }
        }
      }

      return updatedState;
    });
    setFirstPrimaryRowSwitchLoad(false);
  };

  /**
 * Initializes the state of primary row switches and associated properties (tempo, rest, peak power)
 * based on the `content` (calendarItem) data when the component loads.
 * Specifically, it checks for `prescribedValue`,
 * `tempo`, `restTime`, and `trackPeakPower` in the `content` object and enables the corresponding
 * columns in the `primaryRowSwitch` state. Ensures this initialization logic only runs once
 * during the first load by checking the `firstContentLoad` flag.
 *
 * Dependencies:
 * - Runs whenever the `content` object changes.
 */
  useEffect(() => {
    if (content && content.sets) {
      const isTimeType = content.repType === 'time';
      const initialSets = isTimeType
        ? content.sets.map((set) => (
          typeof set.reps === 'string' && set.reps.toUpperCase() === 'AMAP'
            ? { ...set, reps: 'ALAP' }
            : set
        ))
        : content.sets;
      setRows(initialSets);
      const newPrimaryRowSwitch = { ...primaryRowSwitch };
      // Explicitly set both branches (not just the "on" one) - otherwise a
      // property that's already showing stays stuck on after a refetch (e.g.
      // undo) reports it turned back off, since newPrimaryRowSwitch is seeded
      // from the stale prior state above.
      newPrimaryRowSwitch.load = content?.sets.some((set) => !!set.prescribedValue);
      newPrimaryRowSwitch['%pr'] = content?.sets.some((set) => !!set.percent);
      if (content) {
        newPrimaryRowSwitch.tempo = !!content.tempo;
        setTempo(content.tempo || null);
        newPrimaryRowSwitch.rest = !!content.restTime;
        setRest(content.restTime || null);
        newPrimaryRowSwitch['peak power'] = !!content.trackPeakPower;
        setPeakPowerEnum(content.trackPeakPower);
        if (content.repsOnly) {
          newPrimaryRowSwitch.bodyweight = true;
          setIsBodyweight(true);
          setPrimaryRows(
            (prevRows2) => prevRows2.map(
              (row) => (row.name === 'load' ? { ...row, name: 'bodyweight' } : row),
            ),
          );
        } else {
          newPrimaryRowSwitch.bodyweight = false;
          setIsBodyweight(false);
          setPrimaryRows(
            (prevRows2) => prevRows2.map(
              (row) => (row.name === 'bodyweight' ? { ...row, name: 'load' } : row),
            ),
          );
        }
        if (content.repType === 'time') {
          newPrimaryRowSwitch.time = true;
          newPrimaryRowSwitch.reps = false;
          setIsTime(true);
          setPrimaryRows(
            (prevRows2) => prevRows2.map(
              (row) => (row.name === 'reps' ? { ...row, name: 'time' } : row),
            ),
          );
        } else {
          newPrimaryRowSwitch.time = false;
          newPrimaryRowSwitch.reps = true;
          setIsTime(false);
          setPrimaryRows(
            (prevRows2) => prevRows2.map(
              (row) => (row.name === 'time' ? { ...row, name: 'reps' } : row),
            ),
          );
        }
      }
      if (firstContentLoad) {
        setPrimaryRowSwitch(newPrimaryRowSwitch);
        setFirstContentLoad(false);
      }
      setPrimaryRowSwitchState(newPrimaryRowSwitch);
    }
  }, [content]);

  // Format option label with checkmark for selected option (only in dropdown menu)
  const updateColumnName = useCallback((oldName, newName) => {
    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => {
      const newSwitch = { ...prevSwitch };
      // Ensure load and bodyweight are mutually exclusive
      if (newName === 'load') {
        newSwitch.bodyweight = false;
      } else if (newName === 'bodyweight') {
        newSwitch.load = false;
      }
      newSwitch[oldName] = false;
      newSwitch[newName] = true;
      return newSwitch;
    });

    // Wipe out the `load` property in rows if switching from `load` to `bodyweight`
    if (oldName === 'load' && (newName === 'bodyweight')) {
      setRows((prevRows2) => prevRows2.map((row) => {
        const updatedRow = { ...row };
        delete updatedRow[keySwapDictionary.load]; // Remove the load property
        return updatedRow;
      }));
    }
  }, []);

  /** Transposed-layout helper: clamp the set-count to [1, 10] and grow or
   * shrink `rows` accordingly. Grow clones the last row's values for the
   * new sets (matching `addRow` semantics). Shrink silently slices —
   * data in dropped sets is discarded. */
  /** Properties whose input is editable by the coach. Athlete-input-only
   * fields (peak power, velocity, bodyweight) are disabled, so we don't
   * auto-focus them when added. */
  const EDITABLE_FOCUS_PROPS = new Set(['reps', 'time', 'load', '%pr', 'rest', 'tempo']);

  /** Auto-focus the first input of a newly added property. Deferred so
   * the new row has rendered before we query for it. */
  const focusAddedProperty = useCallback((name) => {
    if (!EDITABLE_FOCUS_PROPS.has(name)) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-add-focus="${content.assignedId}-${name}"]`);
      if (el) {
        el.focus();
        if (typeof el.select === 'function') el.select();
      }
    }, 50);
  }, [content.assignedId]);

  const setSetCount = (rawCount) => {
    const parsed = parseInt(rawCount, 10);
    if (Number.isNaN(parsed)) return;
    const target = Math.max(1, Math.min(10, parsed));
    if (target === rows.length) return;

    let updatedRows;
    if (target > rows.length) {
      const lastRow = rows[rows.length - 1] || {};
      const additions = Array.from(
        { length: target - rows.length },
        (_, i) => ({ ...lastRow, setNumber: rows.length + i + 1 }),
      );
      updatedRows = [...rows, ...additions];
    } else {
      updatedRows = rows
        .slice(0, target)
        .map((row, i) => ({ ...row, setNumber: i + 1 }));
    }

    setRows(updatedRows);
    debouncedHandleUpdateLiftItem(
      updatedRows,
      rest,
      tempo,
      peakPowerEnum,
      velocity,
      completionOnly,
      coachCompletion,
      isLeftAndRight,
      trackRepCount,
      trackVolumeLoad,
      isBodyweight,
      isTime,
      forcePrUpdate,
      disablePrUpdate,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      false,
      selectedUpdateOption.value,
    );
  };

  const commitSetsDraft = () => {
    if (setsDraft !== null && setsDraft !== '') {
      setSetCount(Math.min(10, Math.max(1, parseInt(setsDraft, 10))));
    }
    setSetsDraft(null);
  };

  const setTempoAndUpdate = (value) => {
    setTempo(value);
    debouncedHandleUpdateLiftItem(
      rows,
      rest,
      value,
      peakPowerEnum,
      velocity,
      completionOnly,
      coachCompletion,
      isLeftAndRight,
      trackRepCount,
      trackVolumeLoad,
      isBodyweight,
      isTime,
      forcePrUpdate,
      disablePrUpdate,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      false,
      selectedUpdateOption.value,
    );
  };

  const setRestAndUpdate = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
    debouncedHandleUpdateLiftItem(
      rows,
      intRest,
      tempo,
      peakPowerEnum,
      velocity,
      completionOnly,
      coachCompletion,
      isLeftAndRight,
      trackRepCount,
      trackVolumeLoad,
      isBodyweight,
      isTime,
      forcePrUpdate,
      disablePrUpdate,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      false,
      selectedUpdateOption.value,
    );
  };

  const handleInputChange = (rowIndex, field, value) => {
    // Don't convert empty string to null here - let the auto-fill logic handle it
    // if (!value) {
    //   // eslint-disable-next-line no-param-reassign
    //   value = null;
    // }

    // Prevent negative values for numeric fields
    if ((field === 'load' || field === '%pr') && parseFloat(value) < 0) {
      return;
    }

    // %PR must be a non-negative whole number (covers paste/programmatic input)
    if (field === '%pr' && value !== '' && !/^\d+$/.test(value)) {
      return;
    }

    // Prevent negative values for reps and time fields
    let normalizedFieldValue = value;
    if (field === 'reps' || field === 'time') {
      const keyword = field === 'time' ? 'ALAP' : 'AMAP';
      // Allow AMAP / ALAP
      if (value && value.toUpperCase() === keyword) {
        // keyword is valid, continue
      } else if (value && value.includes('-') && !value.startsWith('-')) {
        // Check for ranges (e.g., "8-12") but not negative numbers
        const parts = value.split('-');
        if (parts.length === 2) {
          const start = parseInt(parts[0], 10);
          const end = parseInt(parts[1], 10);
          if (start < 0 || end < 0) {
            return; // Prevent negative values in ranges
          }
        }
      } else if (value && value.includes('+') && !value.startsWith('-')) {
        // Check for minimum values (e.g., "8+") but not negative numbers
        const numPart = value.replace('+', '');
        if (parseInt(numPart, 10) < 0) {
          return; // Prevent negative minimum values
        }
      } else if (value && !Number.isNaN(Number(value))) {
        // Check for simple numbers (including negative ones)
        if (parseFloat(value) < 0) {
          return; // Prevent negative simple numbers
        }
      }

      // Normalize AMAP/ALAP case-drift ("Amap" -> "AMAP"). Invalid keyword variants
      // ("AMRAP") are detected at the row-level gate below to block the API call.
      if (value !== '' && value !== null && value !== undefined) {
        const tokenResult = normalizeRepToken(String(value), [keyword]);
        if (tokenResult.isValid) {
          normalizedFieldValue = tokenResult.value;
        }
      }
    }

    setRows((prevRows2) => {
      const fieldKey = keySwapDictionary[field];

      // Use normalized value (e.g., "Amap" -> "AMAP") for storage and submission
      const writeValue = (field === 'reps' || field === 'time') ? normalizedFieldValue : value;

      // Check if this is the first set and we should auto-fill
      const shouldAutoFill = rowIndex === 0;

      // Convert empty string to null for storage. For `percent` (%PR), drop the
      // key entirely on empty so the JSON sent to the server omits it per-set —
      // mirrors ExerciseTable's handling. Without this, a server-side default
      // backfill would overwrite the cleared value on the round-trip.
      const finalValue = writeValue === '' ? null : writeValue;
      const buildRow = (row) => {
        const next = { ...row, [fieldKey]: finalValue };
        if (fieldKey === 'percent' && (finalValue === null || finalValue === '')) {
          delete next.percent;
        }
        return next;
      };

      let updatedRows;
      if (shouldAutoFill) {
        // Get the current value in the first set
        const currentFirstSetValue = prevRows2[0][fieldKey];

        // Check if all other sets for this field are empty OR have the same value as the current first set
        const otherSetsAreEmptyOrMatchCurrent = prevRows2.slice(1).every((row) => {
          const fieldValue = row[fieldKey];
          return !fieldValue || fieldValue === null || fieldValue === '' || fieldValue === currentFirstSetValue;
        });

        if (otherSetsAreEmptyOrMatchCurrent) {
          // Auto-fill all sets with the same value
          updatedRows = prevRows2.map(buildRow);
        } else {
          // Only update the current row
          updatedRows = prevRows2.map((row, i) => (
            i === rowIndex ? buildRow(row) : row
          ));
        }
      } else {
        // Normal update - only change the current row
        updatedRows = prevRows2.map((row, i) => (
          i === rowIndex ? buildRow(row) : row
        ));
      }

      // Check if any reps/time field has empty values before making API call
      // Prevent API calls if any reps fields are empty (this prevents re-population)
      const repsFieldKey = keySwapDictionary.reps; // This maps to 'reps' for both 'reps' and 'time' fields
      const hasEmptyRepsFields = updatedRows.some((row) => {
        const repsValue = row[repsFieldKey];
        return !repsValue || repsValue === null || repsValue === '';
      });

      // Block API call only when the user is editing reps/time and their just-typed
      // token is invalid (e.g., "AMRAP"). Silent block — matches the existing
      // negative-number guards above; per-keystroke toasts would spam the UI.
      // Derive the keyword from `field`, not `isTime`, to stay consistent with the
      // keyword used by the normalization step earlier in this handler.
      const repsKeyword = field === 'time' ? 'ALAP' : 'AMAP';
      const editedRepsTokenIsInvalid = (field === 'reps' || field === 'time')
        && value !== '' && value !== null && value !== undefined
        && !normalizeRepToken(String(value), [repsKeyword]).isValid;

      // Check if any other field has empty values (excluding 'load' and '%pr' which can be empty per-set)
      const hasEmptyOtherFields = field !== 'load' && field !== '%pr' && field !== 'reps' && field !== 'time' && updatedRows.some((row) => {
        const fieldValue = row[fieldKey];
        return !fieldValue || fieldValue === null || fieldValue === '';
      });

      // Only call the API if no reps fields are empty and no other fields are empty
      if (!hasEmptyRepsFields && !hasEmptyOtherFields && !editedRepsTokenIsInvalid) {
        debouncedHandleUpdateLiftItem(
          updatedRows,
          rest,
          tempo,
          peakPowerEnum,
          velocity,
          completionOnly,
          coachCompletion,
          isLeftAndRight,
          trackRepCount,
          trackVolumeLoad,
          isBodyweight,
          isTime,
          forcePrUpdate,
          disablePrUpdate,
          content,
          index,
          nonSwappedItemFromParentCalendar,
          false,
          selectedUpdateOption.value,
        );
      }

      return updatedRows;
    });
  };

  // Helper function to validate key input for numbers-only fields
  // Pass allowRangeNotation=true for load (range/+ notation allowed)
  const isValidNumberKey = (key, currentValue, allowRangeNotation = false, allowDecimal = true) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true; // These will be handled by the browser with Ctrl modifier
    }

    // Allow decimal point; for range fields allow one per segment (split by '-')
    if (key === '.' && allowDecimal) {
      if (allowRangeNotation) {
        const currentSegment = currentValue.split('-').pop();
        if (!currentSegment.includes('.')) return true;
      } else if (!currentValue.includes('.')) {
        return true;
      }
    }

    // Allow digits
    if (/^\d$/.test(key)) {
      return true;
    }

    // Allow dash (range) and plus notation for range fields
    if (allowRangeNotation && (key === '-' || key === '+')) {
      return true;
    }

    return false;
  };

  // Helper function to validate key input for reps fields (allows AMAP and ranges)
  const isValidRepsKey = (key) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true; // These will be handled by the browser with Ctrl modifier
    }

    // Allow digits
    if (/^\d$/.test(key)) {
      return true;
    }

    // Allow letters for AMAP
    if (/^[a-zA-Z]$/.test(key)) {
      return true;
    }

    // Allow dash for ranges (8-12) and plus for minimums (8+)
    if (key === '-' || key === '+') {
      return true;
    }

    return false;
  };

  const columnInputResolver = (originalName, rowIndex, row) => {
    let name = originalName;
    if (name === 'time') {
      // eslint-disable-next-line no-param-reassign
      name = 'reps';
    }
    const fieldValue = row[keySwapDictionary[name]];
    if (name === 'tempo') {
      return (
        <RowValueContainer>
          <RowValue
            data-add-focus={`${content.assignedId}-tempo`}
            value={tempo}
            onChange={(e) => setTempoAndUpdate(e.target.value)}
            background={colors.surfaceInput}
          />
        </RowValueContainer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <RowValue
            data-add-focus={`${content.assignedId}-rest`}
            type='number'
            value={rest}
            onChange={(e) => setRestAndUpdate(e.target.value)}
            onKeyDown={(e) => {
              // Prevent commas and validate for numbers-only field
              if (e.key === ',' || (!e.ctrlKey && !isValidNumberKey(e.key, e.target.value))) {
                e.preventDefault();
              }
            }}
            background={colors.surfaceInput}
          />
        </RowValueContainer>
      );
    }
    if (name === 'peak power') {
      if (peakPowerEnum === 1) {
        return (
          <RowValueContainer>
            <RowValue
              disabled
              placeholder='-'
              textColor={colors.textTertiary}
              background={colors.surfaceInput}
            />
          </RowValueContainer>
        );
      }
      return (
        <RowValueContainer>
          <RowValue
            disabled
            placeholder='Left'
            textColor={colors.textTertiary}
            style={{ marginRight: 8 }}
            background={colors.surfaceInput}
          />
          <RowValue
            disabled
            placeholder='Right'
            textColor={colors.textTertiary}
            style={{ marginLeft: 8 }}
            background={colors.surfaceInput}
          />
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <RowValue
          type='text'
          aria-label={`${name} for set ${rowIndex + 1}`}
          /** Focus target for auto-focusing a newly added property's first
           * set input. Only editable fields get it (velocity/bodyweight
           * are athlete-input-only/disabled). Keyed by the original
           * property name so it matches the toggled property. */
          data-add-focus={
            rowIndex === 0 && originalName !== 'velocity' && originalName !== 'bodyweight'
              ? `${content.assignedId}-${originalName}`
              : undefined
          }
          value={name === 'bodyweight' ? '' : (fieldValue ?? '')} // null/undefined -> '' for React input; keep legit 0
          onChange={(e) => {
            handleInputChange(rowIndex, name, e.target.value);
          }}
          onKeyDown={(e) => {
            // Always prevent commas
            if (e.key === ',') {
              e.preventDefault();
              return;
            }

            // Skip validation for disabled fields
            if (name === 'velocity' || name === 'bodyweight') {
              return;
            }

            // If the user is mutating an AMAP/ALAP token they're sitting in the
            // middle of, wipe the whole token. This subsumes the right-edge
            // Backspace shortcut that used to live below.
            if (name === 'reps' || name === 'time' || originalName === 'time') {
              const keyword = (name === 'time' || originalName === 'time') ? 'ALAP' : 'AMAP';
              const { value, selectionStart, selectionEnd } = e.target;
              const wipeResult = wipeRepKeywordOnMutation(e, value, selectionStart, selectionEnd, [keyword], { backspaceDeletes: true });
              if (wipeResult.wiped) {
                e.preventDefault();
                e.target.value = wipeResult.newValue;
                handleInputChange(rowIndex, name, wipeResult.newValue);
                const inputEl = e.target;
                requestAnimationFrame(() => {
                  inputEl.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
                });
                return;
              }

              // Single-cell input: once the cell holds an AMAP/ALAP keyword,
              // block any printable-char keystroke that would append/prepend
              // (e.g., produce "AMAP8"). Wipe-on-edit was handled above; reaching
              // here with no selection means the caret is sitting at an edge —
              // but a full-token selection also reaches here (deliberately
              // allowed above), so only block the no-selection append/prepend case.
              const cellTrimmed = (value || '').trim().toUpperCase();
              if (cellTrimmed === keyword
                  && selectionStart === selectionEnd
                  && typeof e.key === 'string'
                  && e.key.length === 1
                  && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                return;
              }
            }

            // Handle keyword autocomplete (AMAP for reps, ALAP for time) on typing 'a' in empty field
            if ((name === 'reps' || name === 'time' || originalName === 'time') && e.key.toLowerCase() === 'a' && (!e.target.value || e.target.value === '')) {
              const keyword = (name === 'time' || originalName === 'time') ? 'ALAP' : 'AMAP';
              e.preventDefault();
              e.target.value = keyword;
              handleInputChange(rowIndex, name, keyword);
              return;
            }

            // Use different validation for reps vs other fields
            const isValid = (name === 'reps' || originalName === 'time')
              ? isValidRepsKey(e.key)
              : isValidNumberKey(e.key, e.target.value, RANGE_FIELDS.has(name), !INTEGER_FIELDS.has(name));

            // Allow Ctrl combinations
            if (!e.ctrlKey && !isValid) {
              e.preventDefault();
            }
          }}
          disabled={name === 'velocity' || name === 'bodyweight'}
          placeholder={name === 'velocity' || name === 'bodyweight' ? '-' : null}
          textColor={name === 'velocity' || name === 'bodyweight' ? colors.textTertiary : colors.text}
          background={colors.surfaceInput}
          data-focus-target={
            // Add focus target to the first editable field that's visible and in the first row
            rowIndex === 0 && (
              (name === 'reps' && primaryRowSwitch.reps)
              || (name === 'time' && primaryRowSwitch.time)
              || (name === 'load' && primaryRowSwitch.load && !primaryRowSwitch.reps && !primaryRowSwitch.time)
            )
              ? content.assignedId
              : undefined
          }
        />
      </RowValueContainer>
    );
  };

  const propertyTooltipId = (name) => {
    if (name === 'reps') return repsTooltipId;
    if (name === 'time') return timeTooltipId;
    if (name === 'peak power') return peakPowerTooltipId;
    if (name === 'velocity') return velocityTooltipId;
    if (name === 'rest') return restTooltipId;
    return undefined;
  };

  return (
    <Wrapper
      ref={tableContainerRef}
      onBlur={nonSwappedItemFromParentCalendar
        ? () => flushDebounceForTable(content.assignedId)
        : undefined}
    >
      <SetsCountHeader>
        <SetsFieldGroup>
          <SetsCountLabel>Sets</SetsCountLabel>
          <SetsStepper>
            <StepperButton
              type='button'
              aria-label='Decrease sets'
              onClick={() => setSetCount(rows.length - 1)}
              disabled={rows.length <= 1}
            >
              −
            </StepperButton>
            <StepperInput
              type='text'
              inputMode='numeric'
            /** While focused, show the in-progress draft; otherwise mirror the
             * committed row count. */
              value={setsDraft !== null ? setsDraft : rows.length}
            /** Select-on-focus so a tabbed-in value is cleanly replaceable. */
              onFocus={(e) => {
              setSetsDraft(String(rows.length));
              e.target.select();
            }}
              onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, '');
              /** Empty mid-edit: keep the field empty until the user types or
               * blurs (blur reverts to the committed count). */
              if (raw === '') {
                setSetsDraft('');
                return;
              }
              let value = parseInt(raw, 10);
              /** Max is 10, so anything larger means the user typed a new
               * digit onto the existing count (e.g. 4 then 5 -> "45").
               * Treat it as replacing with the last digit typed -> 5. "10"
               * stays 10. Gives type-to-replace without caret tracking,
               * and typing 1 then 0 still reaches 10. */
              if (value > 10) {
                value = parseInt(raw.slice(-1), 10);
              }
              /** Only update the draft here — the actual set count (which adds
               * or removes rows) is committed once on blur/Enter to avoid a
               * transient collapse that would wipe per-set values. */
              setSetsDraft(String(Math.min(10, Math.max(1, value))));
            }}
              onBlur={commitSetsDraft}
              onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.target.blur();
              }
            }}
              aria-label='Number of sets'
            />
            <StepperButton
              type='button'
              aria-label='Increase sets'
              onClick={() => setSetCount(rows.length + 1)}
              disabled={rows.length >= 10}
            >
              +
            </StepperButton>
          </SetsStepper>
          <SetsSubtext># of Sets: 1-10</SetsSubtext>
        </SetsFieldGroup>
        <StatusBadgeScroller>
          {isLeftAndRight && <StateBadge>Each Side</StateBadge>}
          {coachCompletion && <StateBadge>Coach Completion</StateBadge>}
          {completionOnly && <StateBadge>Completion Only</StateBadge>}
          {forcePrUpdate && <StateBadge>Force PR</StateBadge>}
          {disablePrUpdate && <StateBadge>PR Off</StateBadge>}
          {trackVolumeLoad && <StateBadge>Track Volume</StateBadge>}
          {trackRepCount && <StateBadge>Track Reps</StateBadge>}
        </StatusBadgeScroller>
      </SetsCountHeader>

      <TransposedScroll>
        <TransposedTable>
          <TransposedHeaderRow>
            <PropertyLabelCell />
            {rows.map((_, setIdx) => (
              <SetHeaderCell key={`set-header-${setIdx}`}>
                {`Set ${setIdx + 1}`}
              </SetHeaderCell>
            ))}
          </TransposedHeaderRow>

          {primaryRows.map((primaryRow) => {
            if (primaryRow.name === 'set') return null;
            if (!primaryRowSwitch[primaryRow.name]) return null;

            const tooltipId = propertyTooltipId(primaryRow.name);

            return (
              <TransposedRow key={`prop-${primaryRow.name}`}>
                <PropertyLabelCell>
                  <NameAndDropdown>
                    <SwapPropDropdown
                      setPrimaryRowSwitch={setPrimaryRowSwitch}
                      primaryRowSwitch={primaryRowSwitch}
                      rowName={primaryRow.name}
                      size={12}
                      updateColumnName={updateColumnName}
                      setPeakPowerEnum={setPeakPowerEnum}
                      peakPowerEnum={peakPowerEnum}
                      name={primaryRow.name === 'load'
                        ? capitalizeWords('Load')
                        : primaryRow.label || capitalizeWords(primaryRow.name)}
                      /** A tag spans exercises with mixed units, so there is
                       * no single load unit to label — same as `TagTable`. */
                      unit={null}
                      setIsBodyweight={setIsBodyweight}
                      setIsTime={setIsTime}
                    />
                    {tooltipId && (
                      <IcomoonReact
                        iconSet={iconSet}
                        size={14}
                        icon='info2'
                        color={colors.textSecondary}
                        style={{ cursor: 'pointer', marginLeft: 4 }}
                        data-tooltip-id={tooltipId}
                        tabIndex={-1}
                        focusable='false'
                        aria-hidden='true'
                      />
                    )}
                  </NameAndDropdown>
                </PropertyLabelCell>

                {rows.map((row, rowIndex) => (
                  <SetCell key={`cell-${primaryRow.name}-${rowIndex}`}>
                    {columnInputResolver(primaryRow.name, rowIndex, row)}
                  </SetCell>
                ))}
              </TransposedRow>
            );
          })}
        </TransposedTable>
      </TransposedScroll>

      <TransposedFooter>
        {showAddColumnButton && (
          <StaticAddColumnDropdownComponent
            primaryRows={primaryRows}
            setPrimaryRows={setPrimaryRows}
            primaryRowSwitch={primaryRowSwitch}
            setPrimaryRowSwitch={setPrimaryRowSwitch}
            setPrimaryRowSwitchState={setPrimaryRowSwitchState}
            size={12}
            containerRef={tableContainerRef}
            assignedId={content.assignedId}
            closeOnSelect
            onPropertyAdded={focusAddedProperty}
            trigger={(
              <AddPropertyTrigger>
                <AddPropertyTriggerIcon>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={10}
                    icon='plus'
                    color='white'
                  />
                </AddPropertyTriggerIcon>
                Add Property
              </AddPropertyTrigger>
            )}
          />
        )}
      </TransposedFooter>

      {/* ReactTooltip components */}
      <ReactTooltip
        id={repsTooltipId}
        place='top'
        positionStrategy='fixed'
        appendTo={document.body}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '250px',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>
              <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text>
            </li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={timeTooltipId}
        place='top'
        positionStrategy='fixed'
        appendTo={document.body}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '250px',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 30)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 30-60)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 30+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;ALAP&quot; for as long as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={peakPowerTooltipId}
        place='top'
        content='Athlete input only'
        positionStrategy='fixed'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />

      <ReactTooltip
        id={velocityTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Athlete input only'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />

      <ReactTooltip
        id={restTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Rest value will be the same for all sets.'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />
    </Wrapper>
  );
};

TagTableTransposed.propTypes = {
  content: PropTypes.instanceOf(Object).isRequired,
  /** Null until a coach picks a replacement tag from the item dropdown. */
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object),
  index: PropTypes.number.isRequired,
  coachCompletion: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  isLeftAndRight: PropTypes.bool.isRequired,
  trackRepCount: PropTypes.bool.isRequired,
  trackVolumeLoad: PropTypes.bool.isRequired,
  forcePrUpdate: PropTypes.bool.isRequired,
  disablePrUpdate: PropTypes.bool.isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  isRecurring: PropTypes.string,
  measure: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.number,
};

TagTableTransposed.defaultProps = {
  swapDropdownSelectedExercise: null,
  isRecurring: '',
  date: null,
  dayNumber: null,
};

export default TagTableTransposed;
